import { memo, useContext, useRef, useState } from 'react'
import { Box, Stack } from '@mui/material'
import { ExtraSmallButton } from '../../../custom'
import { AppContext } from '../../../../App'
import { useSelector } from 'react-redux'
import { selectActiveChartId } from '../../../../selectors/chart'
import * as indicatorProto from '../../../../proto/generated/indicator'
import { DrawingToolbarMenuItem, DrawingToolbarOptionsMenu } from '../drawingToolbarMenuShared'
import { RootState } from '../../../../store'
import OI from '../../../../assets/icons/OI'
import { DrawingToolMenuItem } from './drawingToolMenu'
import ThinArrowUpIcon from '../../../../assets/icons/ThinArrowUpIcon'
import {
    DRAWING_TOOLBAR_CHEVRON_CLASS,
    DRAWING_TOOLBAR_MENU_OPEN_CLASS,
    DrawingToolbarIcon,
    drawingToolbarButtonSx,
    drawingToolbarChevronButtonSx,
    drawingToolbarMenuGroupSx,
    drawingToolbarToolSlotSx
} from '../drawingToolbarShared'

export type OiDrawingToolMenuProps = {
    items: DrawingToolMenuItem[]
    disable: boolean
}

const OiDrawingToolMenu: React.FC<OiDrawingToolMenuProps> = ({ items, disable }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const appContext = useContext(AppContext)
    const activeChartId = useSelector(selectActiveChartId)
    const indicators: indicatorProto.Indicator[] = useSelector((state: RootState) => state.chartStore.indicators)
    const chevronButtonRef = useRef<HTMLButtonElement | null>(null)
    const menuOpen = Boolean(anchorEl)

    const handleClick = () => {
        if (menuOpen) {
            handleClose()
        } else {
            setAnchorEl(chevronButtonRef.current)
        }
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const onIndicatorSelected = (indicator: indicatorProto.Indicator) => {
        if (activeChartId && appContext.chart && appContext.chart[activeChartId]) {
            appContext.chart[activeChartId]?.addIndicator(indicator)
        }
    }

    return (
        <Stack alignItems={'center'} sx={drawingToolbarMenuGroupSx}>
            <Box
                sx={drawingToolbarToolSlotSx}
                className={menuOpen ? DRAWING_TOOLBAR_MENU_OPEN_CLASS : undefined}
            >
                <ExtraSmallButton
                    id={'oi-indicator-menu-button'}
                    aria-label="OI indicators"
                    color={'inherit'}
                    onClick={handleClick}
                    sx={drawingToolbarButtonSx}
                    disabled={disable}
                    data-event={'OI menu clicked from toolbar'}
                >
                    <DrawingToolbarIcon>
                        <OI />
                    </DrawingToolbarIcon>
                </ExtraSmallButton>
                <ExtraSmallButton
                    ref={chevronButtonRef}
                    aria-label="More OI indicators"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    onClick={handleClick}
                    color="inherit"
                    disabled={disable}
                    className={DRAWING_TOOLBAR_CHEVRON_CLASS}
                    data-event={'OI menu clicked from toolbar'}
                    sx={drawingToolbarChevronButtonSx(menuOpen)}
                >
                    <ThinArrowUpIcon sx={{ fontSize: '12px' }} />
                </ExtraSmallButton>
            </Box>
            <DrawingToolbarOptionsMenu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleClose}
                estimatedWidth={220}
            >
                {items.map((i) => (
                    <DrawingToolbarMenuItem
                        key={`split-horizontal-${i.text}`}
                        icon={i.icon}
                        label={i.text === 'OI Historical Profile' ? 'OI Heatmap' : i.text}
                        onClick={() => {
                            const indicator = indicators.find((indicator) => indicator.code === i.code)
                            if (indicator) {
                                onIndicatorSelected(indicator)
                            }
                            handleClose()
                        }}
                        data-event={`Selected ${i.text} from OI menu`}
                    />
                ))}
            </DrawingToolbarOptionsMenu>
        </Stack>
    )
}

export default memo(OiDrawingToolMenu)
